/**
 * Cross-navigation handoff for "open the Playbook drawer on arrival" (WEB-10287).
 *
 * The offer banner lives at the bottom of a routine's conversation, but the
 * drawer that accepts it lives in the routines list. Accepting therefore
 * navigates, and a window event would race: the routines list has not mounted
 * its listener when the click fires. Same shape and same reason as
 * [[playbookRunSignal]] — the click marks the routine, the list consumes the
 * mark once it is up.
 *
 * One-shot, so a later visit to the list never re-opens a drawer nobody asked
 * for.
 *
 * Dismissal is separate and lives in `localStorage`: it is a per-person nudge,
 * not shared state, and keeping it client-side avoids an API and a migration for
 * something whose worst failure is re-showing a suggestion on a new browser.
 */
let pendingRoutineId: number | null = null;

const DISMISS_KEY = 'ws-playbook-offer-dismissed';

export function markPlaybookOfferAccepted(routineId: number): void {
	pendingRoutineId = routineId;
}

/** Returns the routine id once, then clears it. */
export function consumePlaybookOfferAccepted(): number | null {
	const id = pendingRoutineId;
	pendingRoutineId = null;
	return id;
}

/** Read the dismissed set. Storage can throw (private mode, blocked cookies). */
function readDismissed(): number[] {
	try {
		const raw = window.localStorage.getItem(DISMISS_KEY);
		const parsed = raw ? JSON.parse(raw) : [];
		return Array.isArray(parsed) ? parsed.filter((n): n is number => typeof n === 'number') : [];
	} catch {
		return [];
	}
}

export function isPlaybookOfferDismissed(routineId: number): boolean {
	return readDismissed().includes(routineId);
}

export function dismissPlaybookOffer(routineId: number): void {
	try {
		const next = [...new Set([...readDismissed(), routineId])];
		window.localStorage.setItem(DISMISS_KEY, JSON.stringify(next));
	} catch {
		// Storage unavailable — the offer reappears next visit, which is the
		// harmless direction to fail in.
	}
}
